/**
 * Resilient Fetch Utility
 *
 * Provides retry logic with exponential backoff for transient network errors.
 * Used to make API calls more resilient to temporary failures.
 */

export interface RetryOptions {
  maxRetries?: number
  initialDelayMs?: number
  maxDelayMs?: number
  backoffMultiplier?: number
  retryableErrors?: (error: unknown) => boolean
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
  retryableErrors: isRetryableError
}

/**
 * Check if an error is retryable (network errors, timeouts, 5xx responses)
 */
export function isRetryableError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    // Network error (no internet, DNS failure, etc.)
    return true
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase()

    // Network-related errors
    if (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('aborted') ||
      message.includes('failed to fetch') ||
      message.includes('econnrefused') ||
      message.includes('econnreset') ||
      message.includes('etimedout') ||
      message.includes('socket hang up')
    ) {
      return true
    }

    // HTTP 5xx errors (server errors are often transient)
    if (message.includes('500') || message.includes('502') || message.includes('503') || message.includes('504')) {
      return true
    }
  }

  // Check for Response objects with server errors
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status
    return status >= 500 && status < 600
  }

  return false
}

/**
 * Wait for a specified amount of time
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Execute a function with retry logic and exponential backoff
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options?: RetryOptions
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  let lastError: unknown
  let delayMs = opts.initialDelayMs

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error

      // If this was the last attempt or error is not retryable, throw immediately
      if (attempt === opts.maxRetries || !opts.retryableErrors(error)) {
        throw error
      }

      // Log retry attempt (only in development)
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[withRetry] Attempt ${attempt + 1} failed, retrying in ${delayMs}ms...`, error)
      }

      // Wait before retry with exponential backoff
      await delay(delayMs)
      delayMs = Math.min(delayMs * opts.backoffMultiplier, opts.maxDelayMs)
    }
  }

  throw lastError
}

/**
 * Result type for resilient parallel fetches
 */
export type ResilientResult<T> =
  | { success: true; data: T }
  | { success: false; error: Error }

/**
 * Execute multiple fetches in parallel with individual resilience
 * Each fetch gets retry logic, and partial failures don't block others
 */
export async function resilientParallelFetch<T extends Record<string, () => Promise<unknown>>>(
  fetchers: T,
  options?: RetryOptions
): Promise<{
  [K in keyof T]: ResilientResult<Awaited<ReturnType<T[K]>>>
}> {
  const keys = Object.keys(fetchers) as (keyof T)[]

  const results = await Promise.allSettled(
    keys.map(key => withRetry(fetchers[key], options))
  )

  const output = {} as {
    [K in keyof T]: ResilientResult<Awaited<ReturnType<T[K]>>>
  }

  keys.forEach((key, index) => {
    const result = results[index]
    if (result.status === 'fulfilled') {
      output[key] = { success: true, data: result.value as Awaited<ReturnType<T[typeof key]>> }
    } else {
      output[key] = {
        success: false,
        error: result.reason instanceof Error ? result.reason : new Error(String(result.reason))
      }
    }
  })

  return output
}

/**
 * Simple in-memory cache for dashboard data
 */
const cache = new Map<string, { data: unknown; timestamp: number }>()

export function getCached<T>(key: string, maxAgeMs: number = 5 * 60 * 1000): T | null {
  const entry = cache.get(key)
  if (!entry) return null

  const age = Date.now() - entry.timestamp
  if (age > maxAgeMs) {
    cache.delete(key)
    return null
  }

  return entry.data as T
}

export function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() })
}

export function clearCache(key?: string): void {
  if (key) {
    cache.delete(key)
  } else {
    cache.clear()
  }
}
